import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface PullToRefreshProps {
  onRefresh: () => Promise<unknown> | void;
  children: ReactNode;
  threshold?: number;
  maxPull?: number;
}

const TRIGGER_DEFAULT = 70;
const MAX_DEFAULT = 110;

export function PullToRefresh({
  onRefresh,
  children,
  threshold = TRIGGER_DEFAULT,
  maxPull = MAX_DEFAULT,
}: PullToRefreshProps) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef<number | null>(null);
  const activeRef = useRef(false);
  const refreshingRef = useRef(false);
  const pullRef = useRef(0);

  const setPullBoth = useCallback((v: number) => {
    pullRef.current = v;
    setPull(v);
  }, []);

  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (refreshingRef.current) return;
    if (window.scrollY > 0) {
      startYRef.current = null;
      activeRef.current = false;
      return;
    }
    startYRef.current = e.touches[0].clientY;
    activeRef.current = false;
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (startYRef.current === null) return;
      if (window.scrollY > 0) {
        startYRef.current = null;
        activeRef.current = false;
        setPullBoth(0);
        return;
      }
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0) {
        if (activeRef.current) setPullBoth(0);
        activeRef.current = false;
        return;
      }
      activeRef.current = true;
      const damped = Math.min(maxPull, dy * 0.5);
      setPullBoth(damped);
      if (e.cancelable) e.preventDefault();
    },
    [maxPull, setPullBoth]
  );

  const handleTouchEnd = useCallback(() => {
    if (refreshingRef.current) return;
    const wasActive = activeRef.current;
    const distance = pullRef.current;
    startYRef.current = null;
    activeRef.current = false;
    if (wasActive && distance >= threshold) {
      refreshingRef.current = true;
      setRefreshing(true);
      setPullBoth(threshold);
      Promise.resolve(onRefreshRef.current()).finally(() => {
        refreshingRef.current = false;
        setRefreshing(false);
        setPullBoth(0);
      });
    } else {
      setPullBoth(0);
    }
  }, [threshold, setPullBoth]);

  useEffect(() => {
    if (!isTouchDevice) return;
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isTouchDevice, handleTouchStart, handleTouchMove, handleTouchEnd]);

  const visible = pull > 0 || refreshing;
  const reached = pull >= threshold;
  const indicatorY = Math.max(0, pull);
  const opacity = Math.min(1, pull / threshold);

  return (
    <>
      {isTouchDevice && (
        <div
          aria-hidden={!visible}
          className="pointer-events-none fixed left-0 right-0 top-0 z-40 flex justify-center"
          style={{
            transform: `translateY(${indicatorY - 40}px)`,
            opacity: refreshing ? 1 : opacity,
            transition: refreshing || pull === 0 ? "transform 200ms ease-out, opacity 200ms ease-out" : "none",
          }}
        >
          <div className="mt-2 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md border border-border/60">
            {refreshing ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : (
              <ArrowDown
                className={cn(
                  "h-5 w-5 text-primary transition-transform duration-200",
                  reached && "rotate-180"
                )}
              />
            )}
          </div>
        </div>
      )}
      <div
        style={{
          transform: visible ? `translateY(${indicatorY}px)` : undefined,
          transition: refreshing || pull === 0 ? "transform 200ms ease-out" : "none",
        }}
      >
        {children}
      </div>
    </>
  );
}
